import React, {
  forwardRef,
  type ComponentType,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type RefAttributes,
  type PropsWithChildren,
} from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';
import kebabCase from 'lodash/kebabCase';

export type DataElementPageLocation = 'topNav' | 'siteNav' | 'blogNav' | 'hero' | 'body' | 'topFooter' | 'bottomFooter';

export interface WithDecoratedProps {
  /** The component element page location that gets added to data-element-page-location. */
  dataElementPageLocation?: DataElementPageLocation;
  /** The component element type that gets added to data-element-type. */
  dataElementType?: string;
}

/**
 * A generic used to set the return type for the decorate function
 *
 * @typeParam P is the Component props.
 * @typeParam C is the Component SubComponents defined as static methods.
 */
type DecoratedComponent<P, C = P> = ForwardRefExoticComponent<
  PropsWithoutRef<P & WithDecoratedProps> & RefAttributes<HTMLElement>
> &
  C;

const getDisplayName = <T,>(Component: ComponentType<PropsWithChildren<T>>): string => {
  const componentName = Component.displayName || Component.name || 'Decorate';
  const formattedComponentName = componentName.replace('Component', '');
  return formattedComponentName;
};

/**
 * A higher order component for appending data attributes
 *
 * @typeParam P is the Component props.
 * @typeParam C is the Component SubComponents defined as static methods.
 * @param Component
 * @returns Component appended with 'data-element-page-location' and 'data-element-type' attributes
 */
const decorate = <P, C = P>(Component: ComponentType<PropsWithChildren<P>>): DecoratedComponent<P, C> => {
  const WithDecoratedComponent = forwardRef<HTMLElement, P & C & WithDecoratedProps>(
    ({ dataElementPageLocation = 'body', dataElementType, ...rest }, forwardedRef) => (
      <Component
        ref={forwardedRef}
        {...(rest as unknown as P)}
        data-element-page-location={dataElementPageLocation}
        data-element-type={kebabCase(dataElementType || getDisplayName(Component))}
      />
    ),
  );

  WithDecoratedComponent.displayName = getDisplayName(Component);

  hoistNonReactStatics(WithDecoratedComponent, Component);

  // NOTE: This is needed to cast as the expected type because the hoisting of static methods is not getting
  // properly inferred on the return type as we are returning the wrapped component,
  // and hoisting the static methods at different places.
  return WithDecoratedComponent as DecoratedComponent<P, C>;
};

export { decorate };
export default decorate;
